import { useState, useRef, ChangeEvent } from "react"
import { Button, Tooltip } from "antd"
import {
  EditOutlined,
  DeleteOutlined,
  PictureOutlined,
} from "@ant-design/icons"
import {
  UploadPlaceholder,
  UploadPreview,
  PreviewImg,
  Overlay,
  EditBtn,
  DeleteBtn,
} from "./styles"
import { useTranslation } from "react-i18next"

interface IProps {
  defaultImage?: string | null
  image?: string | null
  onImageChange?: (image: string | null) => void
  onFileDataChange?: (file: File | null) => void
}

export default function ImageUpload({
  defaultImage,
  image,
  onImageChange,
  onFileDataChange,
}: IProps) {
  const { t } = useTranslation()
  const [hovered, setHovered] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleUpload = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0]
    if (!file) return
    onFileDataChange?.(file)
    const reader = new FileReader()
    reader.onload = (ev: ProgressEvent<FileReader>) => {
      onImageChange?.(ev.target?.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleEdit = (): void => inputRef.current?.click()

  const handleDelete = (): void => {
    onImageChange?.(null)
    onFileDataChange?.(null)
    if (inputRef.current) inputRef.current.value = ""
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleUpload}
        style={{ display: "none" }}
      />

      {!image && !defaultImage ? (
        <UploadPlaceholder onClick={() => inputRef.current?.click()}>
          <PictureOutlined style={{ fontSize: 32 }} rev={undefined} />
          <span>{t("global.click_to_upload")}</span>
        </UploadPlaceholder>
      ) : (
        <UploadPreview
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {image && <PreviewImg src={image} alt="uploaded" />}
          {defaultImage && !image && (
            <PreviewImg src={defaultImage} alt="uploaded" />
          )}

          <Overlay $visible={hovered}>
            <EditBtn>
              <Tooltip title="Edit">
                <Button
                  shape="circle"
                  icon={<EditOutlined rev={undefined} />}
                  onClick={handleEdit}
                />
              </Tooltip>
            </EditBtn>
            <DeleteBtn>
              <Tooltip title="Delete">
                <Button
                  shape="circle"
                  danger
                  icon={<DeleteOutlined rev={undefined} />}
                  onClick={handleDelete}
                />
              </Tooltip>
            </DeleteBtn>
          </Overlay>
        </UploadPreview>
      )}
    </div>
  )
}
